import { API } from '@ax/api';
import { API_SERVICES } from './utils';
import {
  DeleteEHRAppointmentInput,
  GetAllAppointmentsApiResponse,
  SaveEHRAppointmentInput,
  UpdateEHRAppointmentInput,
  UploadEHRNoteInput,
  getAllAppointmentsParams,
} from './ehr-interface';
import { patchNoteInput } from './appointment-interface';

export const getAllAppointments = async ({
  doctorId,
  date,
}: getAllAppointmentsParams): Promise<GetAllAppointmentsApiResponse[]> => {
  const data = await API.get(API_SERVICES.EHR_API, `/lynx/appointments`, {
    params: {
      doctorId: `${doctorId}`,
      startDate: `${date}`,
      endDate: `${date}`,
      'cache.invalidateCache': true,
    },
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return data.data;
};

export const getAllAppointmentsByConnectedProvider = async ({
  doctorId,
  date,
  adminDoctorId,
}: getAllAppointmentsParams): Promise<GetAllAppointmentsApiResponse[]> => {
  const data = await API.get(API_SERVICES.EHR_API, `/lynx/admin/appointments`, {
    params: {
      doctorId: `${doctorId}`,
      startDate: `${date}`,
      endDate: `${date}`,
      adminDoctorId: `${adminDoctorId}`,
      'cache.invalidateCache': true,
      includeDeleted: true,
    },
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return data.data;
};

export const saveEHRAppointment = async (data: SaveEHRAppointmentInput) => {
  const res = await API.post(API_SERVICES.EHR_API, `/lynx/appointment`, {
    data,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return res.data;
};

export const updateEHRAppointment = async (data: UpdateEHRAppointmentInput) => {
  const res = await API.patch(API_SERVICES.EHR_API, `/lynx/appointment/${data.appointmentId}`, {
    data,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return res.data;
};

export const updateLynxEHRAppointment = async (data: UpdateEHRAppointmentInput) => {
  const res = await API.patch(
    API_SERVICES.EHR_API,
    `/lynx/appointment/update/${data.appointmentId}`,
    {
      data,
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  return res.data;
};

export const deleteEHRAppointment = async (data: DeleteEHRAppointmentInput) => {
  const res = await API.delete(API_SERVICES.EHR_API, `/lynx/appointment/${data.appointmentUuid}`, {
    data,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return res.data;
};

export const patchNote = async (data: patchNoteInput) => {
  const urlWithParams = `/appointments/${data.appointmentId}/notes/${data.appointmentId}/${data.noteStatus}`;
  // This endpoint changes status history but don't update the noteEditingEnabled field
  const res = await API.patch(API_SERVICES.APPOINTMENT_API, urlWithParams, {
    data: {},
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Conditionally execute the second request
  if (data.noteEditingEnabled) {
    // Set noteEditingEnabled to true for the second request
    const updatedData = { noteEditingEnabled: true };
    const res = await API.patch(
      API_SERVICES.APPOINTMENT_API,
      `/go/appointments/${data.appointmentId}/notes/${data.appointmentId}`,
      {
        data: updatedData,
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    return res.data;
  }

  return res.data;
};

export const uploadEHRNote = async (data: UploadEHRNoteInput) => {
  const res = await API.post(API_SERVICES.EHR_API, `/lynx/note/${data.appointmentUuid}`, {
    data: {
      updates: data.updates,
    },
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return res.data;
};

export const sendNoteToMDS = async (data: UploadEHRNoteInput) => {
  const res = await API.post(API_SERVICES.EHR_API, `/lynx/note/${data.appointmentUuid}/mds`, {
    data,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return res.data;
};

export const uploadDataLakeNote = async (data: UploadEHRNoteInput) => {
  const res = await API.post(API_SERVICES.EHR_API, `/lynx/data-lake/note/${data.appointmentUuid}`, {
    data: {
      updates: data.updates,
    },
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return res.data;
};
